/* eslint-disable no-cond-assign */
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@core/injection-tokens';

import { FrontEndLoggerService } from '../../core';
import { AppConfigService } from '../app-config/app-config.service';

declare const braze;
const sdkEndpoint = 'sdk.iad-07.braze.com';
@Injectable({
  providedIn: 'root',
})
export class BrazeService {
  private renderer: Renderer2;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private envConfig: AppConfigService,
    private rendererFactory: RendererFactory2,
    private feLogger: FrontEndLoggerService,
  ) {
    this.init();
  }
  init() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    const brazeSrc = this.envConfig?.braze?.src;
    if (brazeSrc) {
      const brazeScript = this.renderer.createElement('script');
      this.renderer.setAttribute(brazeScript, 'type', 'text/javascript');
      this.renderer.setAttribute(
        brazeScript,
        'textValue',
        `+function(a,p,P,b,y){a.braze={};a.brazeQueue=[];for(var s="BrazeSdkMetadata DeviceProperties Card Card.prototype.dismissCard Card.prototype.removeAllSubscriptions Card.prototype.removeSubscription Card.prototype.subscribeToClickedEvent Card.prototype.subscribeToDismissedEvent Card.fromContentCardsJson ImageOnly CaptionedImage ClassicCard ControlCard ContentCards ContentCards.prototype.getUnviewedCardCount ControlMessage InAppMessage InAppMessage.SlideFrom InAppMessage.ClickAction InAppMessage.DismissType InAppMessage.OpenTarget InAppMessage.ImageStyle InAppMessage.Orientation InAppMessage.TextAlignment InAppMessage.CropType InAppMessage.prototype.closeMessage InAppMessage.prototype.removeAllSubscriptions InAppMessage.prototype.removeSubscription InAppMessage.prototype.subscribeToClickedEvent InAppMessage.prototype.subscribeToDismissedEvent InAppMessage.fromJson FullScreenMessage ModalMessage HtmlMessage SlideUpMessage User User.Genders User.NotificationSubscriptionTypes User.prototype.addAlias User.prototype.addToCustomAttributeArray User.prototype.addToSubscriptionGroup User.prototype.getUserId User.prototype.getUserId User.prototype.incrementCustomUserAttribute User.prototype.removeFromCustomAttributeArray User.prototype.removeFromSubscriptionGroup User.prototype.setCountry User.prototype.setCustomLocationAttribute User.prototype.setCustomUserAttribute User.prototype.setDateOfBirth User.prototype.setEmail User.prototype.setEmailNotificationSubscriptionType User.prototype.setFirstName User.prototype.setGender User.prototype.setHomeCity User.prototype.setLanguage User.prototype.setLastKnownLocation User.prototype.setLastName User.prototype.setPhoneNumber User.prototype.setPushNotificationSubscriptionType User.prototype.setLineId InAppMessageButton InAppMessageButton.prototype.removeAllSubscriptions InAppMessageButton.prototype.removeSubscription InAppMessageButton.prototype.subscribeToClickedEvent FeatureFlag FeatureFlag.prototype.getStringProperty FeatureFlag.prototype.getNumberProperty FeatureFlag.prototype.getBooleanProperty FeatureFlag.prototype.getImageProperty FeatureFlag.prototype.getJsonProperty FeatureFlag.prototype.getTimestampProperty Banner Banner.prototype.getStringProperty Banner.prototype.getNumberProperty Banner.prototype.getBooleanProperty Banner.prototype.getImageProperty Banner.prototype.getJsonProperty Banner.prototype.getTimestampProperty automaticallyShowInAppMessages hideContentCards showContentCards showInAppMessage deferInAppMessage toggleContentCards changeUser destroy getDeviceId getDeviceId initialize isPushBlocked isPushPermissionGranted isPushSupported logCardDismissal logContentCardImpressions logContentCardClick logCustomEvent logInAppMessageButtonClick logInAppMessageClick logInAppMessageHtmlClick logInAppMessageImpression logPurchase openSession requestPushPermission removeAllSubscriptions removeSubscription requestContentCardsRefresh refreshFeatureFlags requestImmediateDataFlush enableSDK isDisabled setLogger setSdkAuthenticationSignature addSdkMetadata disableSDK subscribeToContentCardsUpdates subscribeToInAppMessage subscribeToSdkAuthenticationFailures toggleLogging unregisterPush wipeData handleBrazeAction subscribeToFeatureFlagsUpdates getAllFeatureFlags logFeatureFlagImpression requestBannersRefresh insertBanner getAllBanners subscribeToBannersUpdates isInitialized".split(" "),i=0;i<s.length;i++){for(var m=s[i],k=a.braze,l=m.split("."),j=0;j<l.length-1;j++)k=k[l[j]];k[l[j]]=(new Function("return function "+m.replace(/\./g,"_")+"(){window.brazeQueue.push(arguments); return true}"))()}window.braze.getDeferredInAppMessage=function(){return new window.braze.InAppMessage};window.braze.getCachedContentCards=function(){return new window.braze.ContentCards};window.braze.getUser=function(){return new window.braze.User};window.braze.getFeatureFlag=function(){return new window.braze.FeatureFlag};window.braze.getBanner=function(){return new window.braze.Banner};window.braze.newBannerFromJson=function(){return new window.braze.Banner};(y=p.createElement(P)).type='text/javascript';
    y.async=1;(b=p.getElementsByTagName(P)[0]).parentNode.insertBefore(y,b)
  }(window,document,'script');`,
      );
      this.renderer.setAttribute(brazeScript, 'src', brazeSrc);
      this.renderer.appendChild(this.document.head, brazeScript);
      this.renderer.listen(brazeScript, 'load', () => {
        this.initialize();
      });
      // Log an error if the script fails to load
      this.renderer.listen(brazeScript, 'error', (err: Event) => {
        this.feLogger.logError({
          message: 'Failed to load Braze script',
          details: err,
        });
      });
    } else {
      this.feLogger.logError({
        message: 'BrazeService initialized without src defined',
      });
    }
  }
  initialize() {
    const brazeKey = this.envConfig?.braze?.apiKey;
    if (brazeKey) {
      braze?.initialize(brazeKey, {
        baseUrl: sdkEndpoint,
        enableLogging: false, // set to `true` for debugging
        allowUserSuppliedJavascript: false, // set to `true` to support custom HTML messages
      });

      // `openSession` should be called last - after `changeUser` and `automaticallyShowInAppMessages`
      braze.openSession();
    }
  }

  logCustomEvent(
    eventName: string,
    eventProperties?: Record<string, unknown>,
  ): void {
    if (typeof braze !== 'undefined' && braze?.logCustomEvent) {
      braze.logCustomEvent(eventName, eventProperties);
    } else {
      this.retryLogCustomEvent(eventName, eventProperties);
    }
  }

  private retryLogCustomEvent(
    eventName: string,
    eventProperties?: Record<string, unknown>,
    maxAttempts = 10,
    intervalMs = 500,
  ): void {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts++;
      if (typeof braze !== 'undefined' && braze?.logCustomEvent) {
        braze.logCustomEvent(eventName, eventProperties);
        clearInterval(timer);
      } else if (attempts >= maxAttempts) {
        clearInterval(timer);
        this.feLogger.logError({
          message: `Braze SDK not available after ${maxAttempts} attempts to log custom event: ${eventName}`,
        });
      }
    }, intervalMs);
  }
}
